/**
 * Money on the client.
 *
 * The API sends senti as decimal strings, and the client keeps them as strings
 * until the moment they are rendered. Parsing a pool total into a JS number
 * would silently round it — `Number('1234567890123456789')` is not that number.
 */

const SENTI_PER_TZS = 100n;

export function parseSenti(value: string): bigint {
  return /^-?\d+$/.test(value) ? BigInt(value) : 0n;
}

/** Whole shillings with thousands separators. What the prize headline shows. */
export function formatTzs(senti: string | bigint): string {
  const v = typeof senti === 'bigint' ? senti : parseSenti(senti);
  const negative = v < 0n;
  const whole = (negative ? -v : v) / SENTI_PER_TZS;
  return `${negative ? '-' : ''}${whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

/** Two decimal places. Used in the wallet, where the senti matter. */
export function formatTzsExact(senti: string | bigint): string {
  const v = typeof senti === 'bigint' ? senti : parseSenti(senti);
  const negative = v < 0n;
  const abs = negative ? -v : v;
  const whole = (abs / SENTI_PER_TZS).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${whole}.${(abs % SENTI_PER_TZS).toString().padStart(2, '0')}`;
}

/**
 * Countdown as mm:ss under an hour, h:mm:ss above it (spec §7).
 *
 * "and it must actually move" — the caller ticks this once a second off a
 * target timestamp rather than decrementing a stored number, so a backgrounded
 * tab that misses ticks catches up instead of drifting.
 */
export function formatCountdown(secondsRemaining: number): string {
  const s = Math.max(0, Math.floor(secondsRemaining));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}
